import { useMemo } from 'react';
import styled from 'styled-components';

import MoreContentRequester from 'components/MoreContentRequester';
import PortalConnection from 'models/PortalConnection';
import { Body, BodySmall } from 'styles/typography';

import TaskCard from './TaskCard';
import {
  filterPatientCards,
  OperationalTaskCard,
  orderTaskCards,
  PatientTaskCard,
  TaskFilters,
  TaskSort,
} from './taskCards';
import useTasks from './useTasks';

interface Props {
  // Built by the page: the tab count is over the UNFILTERED set (operational cards included),
  // so the page has to hold the cards anyway and building them twice would let the count and
  // the list disagree.
  patientCards: PatientTaskCard[];
  operational: OperationalTaskCard[];
  isLoading: boolean;
  isError: boolean;
  connections: PortalConnection[];
  // Sort and filters are the page's: their controls (the FilterBar and the toolbar band) render
  // outside this tab. This receives only the applied values, plus a reset for the empty state.
  sort: TaskSort;
  filters: TaskFilters;
  // Whether a status or created-date filter is applied — those ride the request rather than
  // sieving the loaded cards, so this tab can't read them off `filters`.
  serverFiltered?: boolean;
  onClearFilters: () => void;
  // The page's infinite-query handle, for the scroll sentinel — the list loads twenty at a
  // time and fetches more as the sentinel scrolls into view.
  query: ReturnType<typeof useTasks>['query'];
  // The open gesture carries the traversal order with it: the filtered, sorted collection lives
  // here, and the slideout's Prev/Next walk THAT list rather than a global one. Passing it at
  // click time (instead of publishing it upward on every render) is what keeps the order the
  // reader was looking at from being replaced by a later refetch's.
  onOpenTask: (workflowId: string, orderedWorkflowIds: string[]) => void;
  onManagePortals: (sourceSystem?: string) => void;
}

export default function TasksTab({
  patientCards,
  operational,
  isLoading,
  isError,
  connections,
  sort,
  filters,
  serverFiltered = false,
  onClearFilters,
  query,
  onOpenTask,
  onManagePortals,
}: Props) {
  const cards = useMemo(
    () => orderTaskCards(operational, filterPatientCards(patientCards, filters), sort),
    [operational, patientCards, filters, sort]
  );

  // Patient cards only: the operational cards pinned above them are portal-level and belong to
  // no task, so an arrow must never land on one.
  const patientOrder = cards.flatMap((card) => (card.kind === 'patient' ? [card.workflowId] : []));

  if (isError) return <Message data-testid='tasks-error'>We couldn’t load your tasks.</Message>;

  const filtered = serverFiltered || filters.sourceSystems.length > 0 || filters.reasons.length > 0;

  return (
    <Wrapper>
      {isLoading ? (
        <Message data-testid='tasks-loading'>Loading tasks…</Message>
      ) : cards.length === 0 ? (
        <EmptyState filtered={filtered} connections={connections} onClearFilters={onClearFilters} />
      ) : (
        <Cards data-testid='task-cards'>
          {cards.map((card) => (
            <TaskCard
              key={card.id}
              card={card}
              onOpen={(workflowId) => onOpenTask(workflowId, patientOrder)}
              onManagePortals={onManagePortals}
            />
          ))}
        </Cards>
      )}
      {/* Outside the cards branch on purpose: a filter can empty the LOADED pages while
          matches still sit on unloaded ones, and the sentinel staying visible is what keeps
          them coming. It renders nothing once the last page is in. */}
      <MoreContentRequester resource={query} />
    </Wrapper>
  );
}

// None of these are drawn. Three cases, and they are genuinely different sentences: a filter
// that matched nothing, an all-clear on a portal that scans itself, and a client doing this by
// hand — which is also the case where the tab bar is degenerate, so the empty state is the only
// thing on the page explaining what it is for.
function EmptyState({
  filtered,
  connections,
  onClearFilters,
}: {
  filtered: boolean;
  connections: PortalConnection[];
  onClearFilters: () => void;
}) {
  if (filtered) {
    return (
      <Message data-testid='tasks-empty-filtered'>
        <Body>No tasks match these filters.</Body>
        <ClearLink type='button' onClick={onClearFilters}>
          Clear filters
        </ClearLink>
      </Message>
    );
  }

  if (connections.some((connection) => connection.canScan)) {
    return (
      <Message data-testid='tasks-empty-scannable'>
        <Body>You’re all caught up.</Body>
        <BodySmall>New tasks appear here as portal scans find referrals that need you.</BodySmall>
      </Message>
    );
  }

  return (
    <Message data-testid='tasks-empty'>
      <Body>No tasks right now.</Body>
      <BodySmall>A task appears here when a referral from a portal needs your attention.</BodySmall>
    </Message>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 48px 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.black75};
`;

const ClearLink = styled.button`
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.blue100};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;
